import { WISHLIST_WIDGET_NAMES } from "@/constants/widget-names";
import { growaveConfigurationToken } from "@/tokens";
import { isShadowRoot } from "@/utils/isShadowRoot/isShadowRoot";
import { WishlistsQuery } from "@apps/wishlist/queries/WishlistsQuery";
import { WishlistsService } from "@apps/wishlist/services/WishlistsService";
import WishlistsStorefrontValuesService from "@apps/wishlist/services/WishlistsStorefrontValuesService";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { I18nModule } from "@modules/i18n/I18nModule";
import { container } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";
import { WidgetInterface } from "@interfaces/WidgetInterface";

import GoToWishlistPageHeaderWidgetComponent from "./GoToWishlistPageHeaderWidget.svelte";
import { GoToWishlistPageHeaderWidgetConfigInterface } from "./GoToWishlistPageHeaderWidgetConfig";

class GoToWishlistPageHeaderWidget implements WidgetInterface {
    private wishlistsQuery: WishlistsQuery;
    private wishlistsService: WishlistsService;
    private wishlistsStorefrontValuesService: WishlistsStorefrontValuesService;

    constructor(
        private readonly config: GoToWishlistPageHeaderWidgetConfigInterface,
        private readonly logger: LoggerInterface
    ) {
        this.logger.debug(
            "GoToWishlistPageHeaderWidget is initialized",
            config
        );

        this.wishlistsQuery = container.resolve(WishlistsQuery);
        this.wishlistsService = container.resolve(WishlistsService);
        this.wishlistsStorefrontValuesService = container.resolve(
            WishlistsStorefrontValuesService
        );

        const i18nModule = container.resolve(I18nModule);
        i18nModule.exports.i18n.registerLoader(
            "wishlist.GoToWishlistPageHeaderWidget",
            i18nModule.exports.i18nApiLoaderFactory.getLoader(
                "wishlist.GoToWishlistPageHeaderWidget"
            )
        );
        i18nModule.exports.i18n.registerLoader(
            "wishlist.critical.GoToWishlistPageHeaderWidget",
            i18nModule.exports.i18nCriticalLoaderFactory.getLoader(
                "wishlist.critical.GoToWishlistPageHeaderWidget"
            )
        );
    }

    async init(): Promise<this> {
        const customizationManager = container.resolve(CustomizationManager);
        const growaveConfiguration = container.resolve(
            growaveConfigurationToken
        );

        const element = this.config.target as HTMLElement;
        element.style.display = "block";

        const shadowRoot = isShadowRoot(
            this.config.isWidgetShadowRootEnabled,
            growaveConfiguration.isShadowRootDisabledGlobally
        )
            ? element.shadowRoot || element.attachShadow({ mode: "open" })
            : null;

        const customizationManagerConfig = {
            widgetName: WISHLIST_WIDGET_NAMES.GO_TO_WISHLIST_PAGE_HEADER_WIDGET,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStyles(customizationManagerConfig);
        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        new GoToWishlistPageHeaderWidgetComponent({
            target: shadowRoot || element,
            props: {
                wishlistsQuery: this.wishlistsQuery,
                wishlistsService: this.wishlistsService,
                heartColor: this.config.heartColor,
                wishlistsStorefrontValuesService:
                    this.wishlistsStorefrontValuesService,
                afterWidgetInject: () => {
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    );
                },
            },
        });

        return this;
    }
}

export default GoToWishlistPageHeaderWidget;
